/*
 * One person, in the details column (UX §2.1). It used to be a dialog over the directory; it is now
 * a view the column can be asked for from anywhere a name or an avatar appears — a directory row, a
 * message, a member list, an administration table.
 *
 * The view renders whatever the server sent and decides nothing itself: `GET /api/users/{id}`
 * answers at the viewer's own rung (security.md §9.6), so a member sees the name, the handle, the
 * title, the pronouns and the zone this person works in, while the address and the last sign-in reach
 * the account itself and an administrator. A field that is absent is not rendered — which is why
 * there is no "hidden for you" row anywhere below.
 *
 * What the card is opened for is the facts, so the facts are what it is built around: a large
 * picture, the name as this view's heading with the role and the handle under it, and then every
 * field the answer carries as a definition list — the label above its value, because a 360-px column
 * has no room for two columns of text. The order is the order the questions are asked in: the local
 * time first, then what this person does, then who they are, then how to reach them, then where the
 * two of you meet. The time ticks with the rest of the interface's clocks (core/i18n/useNow.ts):
 * "is it a decent hour to write to this person" is the question a profile is opened with, and an
 * answer that quietly went stale is worse than none.
 *
 * The shared channels are the one thing here the server was not asked for: they are in the reader's
 * own model already, and a list of the places two people meet is what a profile is opened for
 * (A-11 item 24). The model holds the member ids only while a conversation is small
 * (protocol.md §2.6), so the row appears when they are known and stays away when they are not.
 */

import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react"
import { conversationHref } from "../../routes/links.ts"
import { Button, ListState } from "../../ui/index.ts"
import { errorText } from "../auth/index.ts"
import { formatDateTime, formatTimeIn, isoDateTime } from "../i18n/format.ts"
import { t } from "../i18n/index.ts"
import { useNow } from "../i18n/useNow.ts"
import type { DetailsView } from "../modules/registry.ts"
import { inPlaceClick, useModel } from "../shell/index.ts"
import type { ConversationSlice, UserId } from "../store/model.ts"
import { peopleApi, type PersonView } from "./api.ts"
import { directMessagePath } from "./navigation.ts"
import "./people.css"
import { profileSubtitle } from "./person.ts"
import { chipPerson, PersonHead } from "./PersonChip.tsx"

/** A place two people are both in: how it is addressed, and where it lives. */
interface SharedPlace {
	address: string
	href: string
}

/**
 * The channels and private conversations both accounts belong to, alphabetically. Direct
 * conversations are left out: "you two have a direct conversation" is not news to either of them,
 * and the profile's own *Message* control goes there anyway.
 *
 * A place is named the way every other surface names one — `#slug`, the naming ladder's address of
 * a channel (X-A3 V21). That ladder reads the whole model and this view holds one map out of it, so
 * the chip spells the address out for the two kinds the list is filtered down to.
 */
function sharedPlaces(
	conversations: Record<number, ConversationSlice>,
	meId: UserId,
	otherId: UserId,
): SharedPlace[] {
	if (meId === 0 || meId === otherId) return []
	const places: SharedPlace[] = []
	for (const slice of Object.values(conversations)) {
		const meta = slice.meta
		if (meta.kind !== "channel" && meta.kind !== "private") continue
		const members = meta.memberIds
		if (members === undefined || !members.includes(meId) || !members.includes(otherId)) continue
		const href = conversationHref(meta)
		if (href === null) continue
		places.push({ address: meta.slug === undefined ? (meta.name ?? "") : `#${meta.slug}`, href })
	}
	return places.sort((a, b) => a.address.localeCompare(b.address))
}

export function ProfileView({ params }: { params: Record<string, string | number> }) {
	const userId = Number(params.userId)
	// The answer carries the person it is about, so that the view of somebody else is never the
	// answer of the one before them while the second read is still running.
	const [answer, setAnswer] = useState<{
		id: number
		person?: PersonView
		error?: string
	} | null>(null)
	// A read this view may ask for again: a failed profile is the one state on this surface a reader can
	// do something about, and *Try again* needs a dependency to change.
	const [attempt, setAttempt] = useState(0)
	// Which person's address was copied, rather than a flag: a column that is asked for somebody else
	// shows their card and not the confirmation of the last one, with no effect to reset.
	const [copiedFor, setCopiedFor] = useState<number | null>(null)
	// Two stable selections rather than one that builds a list: a selector returning a fresh array
	// on every read would re-render this view for every event that touches any conversation.
	const conversations = useModel((model) => model.conversations)
	const meId = useModel((model) => model.me?.id ?? 0)
	const places = useMemo(
		() => sharedPlaces(conversations, meId, userId),
		[conversations, meId, userId],
	)
	const now = useNow()

	// Everything the column shows comes from the server: the rung-dependent fields exist nowhere
	// else, and the opener hands over an id rather than a row so that every surface opens the same
	// view with the same one thing.
	useEffect(() => {
		let live = true
		peopleApi.person(userId).then(
			(person) => {
				if (live) setAnswer({ id: userId, person })
			},
			(err: unknown) => {
				if (live) setAnswer({ id: userId, error: errorText(err) })
			},
		)
		return () => {
			live = false
		}
	}, [userId, attempt])

	const shown = answer?.id === userId ? answer : null
	// The skeleton is the shape of what is coming — a face and two lines — and appears only if the read
	// takes longer than the eye notices (`ui/ListState.tsx`).
	if (shown === null) return <ListState.Loading rows={4} />
	const full = shown.person
	if (full === undefined) {
		return (
			<ListState.Error
				message={shown.error ?? ""}
				retry={() => {
					setAnswer(null)
					setAttempt((at) => at + 1)
				}}
			/>
		)
	}

	const messagePath = directMessagePath(full.handle)
	const email = full.email
	// The list is built rather than written out so that a person with nothing to show gets no empty
	// definition list, and so that the order of the rows is one thing to read.
	const facts: [string, ReactNode][] = []
	const fact = (label: string, value: string | undefined) => {
		if (value !== undefined && value !== "") facts.push([label, value])
	}
	// The zone is worth a row as the time it is there, not as an IANA name, and it is the *first* row:
	// the question a profile is opened with is what o'clock it is where this person sits, and that
	// minute moves while the column is open. Every member is sent it (E-01); a zone this build's
	// `Intl` cannot resolve yields no clock, and then there is no row rather than an empty one. The
	// state this person is in is said in words above, on the head the hover card shares
	// (PersonChip.tsx).
	const localTime = full.timezone === undefined ? "" : formatTimeIn(now, full.timezone)
	if (localTime !== "") {
		facts.push([
			t("people.profile.localTime"),
			<time key="localTime" dateTime={isoDateTime(now)}>
				{localTime}
			</time>,
		])
	}
	fact(t("people.profile.jobTitle"), full.title)
	// The full name only where it says something the heading does not. Most accounts have the two
	// set to the same words, and "Full name: Dora Degen" under the heading "Dora Degen" is a row
	// spent on repeating the line above it (X-B1 D14).
	if (full.fullName !== full.displayName) fact(t("people.profile.fullName"), full.fullName)
	fact(t("people.profile.pronouns"), full.pronouns)
	if (email !== undefined && email !== "") {
		facts.push([
			t("people.profile.email"),
			<a key="email" href={`mailto:${email}`}>
				{email}
			</a>,
		])
	}
	if (places.length > 0) {
		facts.push([
			t("people.profile.sharedChannels"),
			<span className="tb-profile__places" key="sharedChannels">
				{places.map((place) => (
					<a
						className="tb-tag"
						key={place.href}
						href={place.href}
						onClick={inPlaceClick(place.href)}
					>
						{place.address}
					</a>
				))}
			</span>,
		])
	}
	if (full.lastLoginAt !== undefined) {
		facts.push([
			t("people.profile.lastLogin"),
			<time key="lastLogin" dateTime={isoDateTime(full.lastLoginAt)}>
				{formatDateTime(full.lastLoginAt)}
			</time>,
		])
	}

	async function copyEmail(address: string) {
		try {
			await navigator.clipboard.writeText(address)
			setCopiedFor(userId)
		} catch {
			// A browser that refuses the clipboard leaves the address on screen, where it is
			// selectable and a mailto: link besides.
			setCopiedFor(null)
		}
	}

	return (
		<div className="tb-profile">
			{/* The name is this view's heading — the pane's own heading says which view it is
			    (UX §2.1) — with what the account is and its handle as its subtitle, so a reader hears
			    "Bea Bergmann" and then what she is, rather than one run of five words (X-A2 P8). The
			    block is the one every surface draws a person with (PersonChip.tsx), at the size a
			    profile deserves; the hover card is its smaller twin. */}
			<PersonHead person={chipPerson(full)} subtitle={profileSubtitle(full)} />
			{facts.length === 0 ? null : (
				<dl className="tb-facts">
					{facts.map(([label, value]) => (
						<Fragment key={label}>
							<dt>{label}</dt>
							<dd>{value}</dd>
						</Fragment>
					))}
				</dl>
			)}
			<div className="tb-actions">
				{/* A control that goes somewhere is a link, and it wears the primary variant like any
				    other first action — it used to be a `<Link>` underlined inside a filled button,
				    which read as two controls in one (A-11 item 23). */}
				<Button
					variant="primary"
					href={messagePath}
					onClick={inPlaceClick(messagePath)}
					label={t("people.message")}
				/>
				{email === undefined || email === "" ? null : (
					<Button
						variant="neutral"
						label={t("people.profile.copyEmail")}
						onClick={() => void copyEmail(email)}
					/>
				)}
			</div>
			{copiedFor === userId ? (
				<p className="tb-status" role="status">
					{t("people.profile.copied")}
				</p>
			) : null}
		</div>
	)
}

/**
 * Core's own details views, handed to the shell by `routes/routes.tsx`. The list lives beside the
 * only view there is rather than in `core/shell/`: the shell renders the views, so a list of them
 * inside it would import the areas that import the shell (UX §2.1).
 */
export const CORE_DETAILS_VIEWS: DetailsView[] = [
	{ id: "core.profile", titleKey: "shell.details.profile", View: ProfileView },
]
